'use client';

import Link from 'next/link';
import { useEffect, useState, type CSSProperties } from 'react';
import { GRID_ENABLED } from '../connections/lib/daily-game';
import {
  ALL_GAMES,
  GAME_META,
  fetchProgress,
  computeSlateNeighbors,
  type DailyGame,
  type SlateNeighbor,
} from './dailyGames';

// Shared cross-game day nav, rendered at the bottom of every play screen.
// Prev/next step one day across the published slate, so they cross game
// boundaries (Monday's Connections → Sunday's Decode going back); the center
// links to the /games hub. Built from the union of every game's /progress
// published dates, so future days never appear and there are no dead links.
// Always rendered — not gated behind finishing the current day's game.

function shortWeekday(date: string): string {
  const dt = new Date(`${date}T12:00:00`); // noon anchor avoids TZ rollover
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', { weekday: 'short' });
}

export function DailySlateNav({
  currentGame: _currentGame,
  date,
  userId,
}: {
  /** The game whose play screen this is (currently unused; kept for clarity
   *  and future per-game styling). */
  currentGame: DailyGame;
  date: string;
  userId?: string;
}) {
  const [neighbors, setNeighbors] = useState<{
    prev: SlateNeighbor | null;
    next: SlateNeighbor | null;
  }>({ prev: null, next: null });

  useEffect(() => {
    const games = ALL_GAMES.filter((g) => g !== 'grid' || GRID_ENABLED);
    let cancelled = false;
    Promise.all(games.map((g) => fetchProgress(g, { userId }))).then(
      (results) => {
        if (cancelled) return;
        const slate: SlateNeighbor[] = [];
        results.forEach((p, i) => {
          if (!p) return;
          for (const e of p.entries) slate.push({ game: games[i], date: e.date });
        });
        setNeighbors(computeSlateNeighbors(slate, date));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [date, userId]);

  const linkStyle: CSSProperties = {
    fontFamily: 'var(--font-sans)',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--fg-2)',
    textDecoration: 'none',
  };

  const { prev, next } = neighbors;

  return (
    <div
      className="mo-bg-card"
      style={{
        marginTop: 16,
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        border: '1px solid var(--border-soft)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--card-stack)',
      }}
    >
      <div style={{ justifySelf: 'start' }}>
        {prev ? (
          <Link href={GAME_META[prev.game].href(prev.date)} style={linkStyle}>
            ← {shortWeekday(prev.date)} · {GAME_META[prev.game].short}
          </Link>
        ) : (
          <span aria-hidden />
        )}
      </div>
      <Link href="/games" style={{ ...linkStyle, color: 'var(--acc-deep)' }}>
        Games
      </Link>
      <div style={{ justifySelf: 'end' }}>
        {next ? (
          <Link href={GAME_META[next.game].href(next.date)} style={linkStyle}>
            {GAME_META[next.game].short} · {shortWeekday(next.date)} →
          </Link>
        ) : (
          <span aria-hidden />
        )}
      </div>
    </div>
  );
}
